import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TryAgainLink } from 'LoggedOut/LoggedOutScreenWrapper/LoggedOutScreenWrapper.styled';
import { useHistory, useLocation } from 'react-router';
import RoutePath from 'App/RoutePath';
import { getChannel } from 'utils';
import { EmailAddress } from '../EmailConfirmation/EmailConfirmation.styled';
import {
  LoggedOutScreenWrapperFooter,
  LoggedOutScreenSubtitle,
  LoggedOutScreenTitle,
  LoggedOutScreenWrapper,
} from '../LoggedOutScreenWrapper';
import {
  EmailConfirmationFooter,
  EmailConfirmationHelperText,
} from '../EmailConfirmation/EmailConfirmationShared';

import './SignUpEmailConfirmation.scss';

export enum EmailConfirmationVariant {
  SignUpLink = 'sign-up-link',
  SignInLink = 'sign-in-link',
}

export interface EmailConfirmationLocationState {
  email?: string;
  redirectTo?: string;
  variant?: EmailConfirmationVariant;
  emailIsMasked?: boolean;
}

export interface SignUpEmailConfirmationProps {
  email?: string;
  redirectTo?: string;
}

const SUBTITLE_KEYS = {
  [EmailConfirmationVariant.SignUpLink]: 'sign_up_link.subtitle',
  [EmailConfirmationVariant.SignInLink]: 'sign_in_link.subtitle',
} as const;

function SignUpEmailConfirmation({
  email,
  redirectTo,
}: SignUpEmailConfirmationProps) {
  const { t: translate } = useTranslation('logged_out', {
    keyPrefix: 'sign_up.sign_up_email_confirmation',
  });
  const history = useHistory();
  const { state } = useLocation<EmailConfirmationLocationState>();
  const userEmail = email || state?.email;
  const redirect = state?.redirectTo || redirectTo;
  const { variant, emailIsMasked } = state || {};
  const hasExistingAccount = variant === EmailConfirmationVariant.SignInLink;

  useEffect(() => {
    if (!userEmail) {
      history.push(RoutePath.Login);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const onRedirect = () => {
    history.push({
      pathname: redirect || RoutePath.Home,
      search: location.search,
      state: {
        ...state,
        email: userEmail,
      },
    });
  };

  return (
    <LoggedOutScreenWrapper dataTestId="v2-email-confirmation-screen">
      <LoggedOutScreenTitle>
        {translate(hasExistingAccount ? 'sign_in_link.heading' : 'heading')}
      </LoggedOutScreenTitle>
      <LoggedOutScreenSubtitle data-testid="email-confirmation-subtitle">
        <Trans
          t={translate}
          i18nKey={(variant && SUBTITLE_KEYS[variant]) || 'subtitle'}
          values={{ email: userEmail }}
          components={{
            email_address: (
              <EmailAddress
                className={variant ? 'confirmation-email-address' : undefined}
              />
            ),
          }}
        />
      </LoggedOutScreenSubtitle>
      {hasExistingAccount && (
        <EmailConfirmationHelperText>
          {translate('sign_in_link.account_email_explainer', {
            brandName: getChannel().brandName,
          })}
        </EmailConfirmationHelperText>
      )}
      {redirect && (
        <EmailConfirmationHelperText>
          <Trans
            t={translate}
            i18nKey="helper_text"
            components={{
              confirmation_link: (
                <TryAgainLink role="link" onClick={onRedirect}>
                  Try again
                </TryAgainLink>
              ),
            }}
          />
        </EmailConfirmationHelperText>
      )}
      {/*
        A masked address (e.g. j***@gmail.com) tells the user which inbox to
        check but isn't one they can necessarily act on, so we don't offer a
        shortcut into it. This is purely a UX choice — the shortcut itself no
        longer carries the address, so nothing here guards the URL.
      */}
      {!emailIsMasked && (
        <LoggedOutScreenWrapperFooter>
          <EmailConfirmationFooter />
        </LoggedOutScreenWrapperFooter>
      )}
    </LoggedOutScreenWrapper>
  );
}

export default SignUpEmailConfirmation;
